import { InputGroup, InputNumber } from "rsuite";
import { Button, Container } from "reactstrap";
import { useParams } from "react-router-dom";
import { useState } from "react";

import useCleanTenantSchemas from "../../../api/api-hooks/tenantSchemas/useCleanTenantSchemas";
import useDeleteTenantSchema from "../../../api/api-hooks/tenantSchemas/useDeleteTenantSchema";
import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import AppTablePagination from "../../../components/AppTable/AppTablePagination";
import AppPageHeader from "../../../components/Headers/AppPageHeader";
import { TenantSchema } from "../../../types/GlobalInterfaces";
import useCloud from "../../../api/api-hooks/cloud/useCloud";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import AppDrawer from "../../../components/Drawers/AppDrawer";
import AppEmptyState from "../../../elements/AppEmptyState";
import ListItem from "../../../components/CRM/ListItem";
import AppSpinner from "../../../elements/AppSpinner";
import IconButton from "../../../elements/IconButton";
import useModule from "../../../hooks/useModule";
import API from "../../../api/api/API";

const TenantSchemasList = () => {
  const { cloudID }: { cloudID: string } | any = useParams();
  const { cloud } = useCloud(cloudID);
  const initialQuery = {
    Aggregations: [],
    Fields: ["Created"],
    Filters: [],
    OrderBy: {
      Direction: "DESC",
      Field: "Created",
    },
    Pagination: {
      Count: 25,
      Page: 1,
    },
    tenantID: cloudID,
  };
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState<boolean>(false);
  const [quantity, setQuantity] = useState<number>(50);
  const { module } = useModule("TenantSchema");

  const { records, pagination, recordsLoading, error } = useQueriedRecords({
    moduleAPIName: "TenantSchema",
    query: query,
    cloudID: cloudID,
  });

  const {
    confirmClearTenantSchemas,
    cleanTenantSchemas,
    cleanTenantSchemasLoading,
  } = useCleanTenantSchemas();

  const {
    confirmDeleteTenantSchema,
    confirmDeleteNewestTenantSchema,
    deleteLastTenantSchema,
    deleteTenantSchema,
  } = useDeleteTenantSchema();

  const handleCleanSchemas = async () => {
    const confirmed = await confirmClearTenantSchemas(quantity);
    if (!confirmed || quantity < 1) return;

    cleanTenantSchemas({ tenantID: cloudID, tenantSchemasQuantity: quantity });
    setOpen(false);
    setQuantity(50);
  };

  const handleDeleteTenantSchema = async (
    tenantSchemaID: string,
    index: number
  ) => {
    let confirmed: boolean;

    if (index === 0 && records?.length === 1) {
      await deleteLastTenantSchema();
      return;
    }

    if (index === 0) {
      confirmed = await confirmDeleteNewestTenantSchema();
    } else {
      confirmed = await confirmDeleteTenantSchema();
    }

    if (!confirmed) return;

    deleteTenantSchema({ tenantID: cloudID, tenantSchemaID });
  };

  const handleExportTenantSchema = async (tenantSchemaID: string) => {
    try {
      const data = await API.exportTenantSchema(cloudID, tenantSchemaID);
      const donwloadURL = data.data.data;
      window.open(donwloadURL, "_blank");
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuantityChange = (qty: string) => {
    const finalQty = Number(qty.replace(".", ""));

    if (qty === "") {
      setQuantity(0);
    }

    setQuantity(finalQty);
  };

  const rows = records.map((record: TenantSchema, index: number) => {
    const createdDate = record?.Created ? new Date(record.Created) : null;
    const formattedDate = createdDate
      ? createdDate.toLocaleDateString(
          "en-US",
          //@ts-ignore
          { day: "numeric", month: "long", year: "numeric" }
        )
      : "N/A";

    return (
      <ListItem
        key={record.id}
        title={record.id}
        subTitle={`Created on ${formattedDate}`}
        icon="mdi-database-refresh"
        optionsButton={
          <div className="tw:flex tw:gap-2">
            <IconButton
              icon="mdi mdi-delete"
              color="danger"
              onClick={() =>
                record.id && handleDeleteTenantSchema(record.id, index)
              }
              disabled={index === 0 && records?.length === 1}
            />
            <IconButton
              icon="mdi mdi-download"
              onClick={() => record.id && handleExportTenantSchema(record.id)}
            />
            {index === 0 && (
              <i
                className="mdi mdi-star"
                style={{ fontSize: "25px", color: "orange" }}
              />
            )}
          </div>
        }
      />
    );
  });

  return (
    <>
      <div className="p-2">
        <div className="header">
          <Container fluid>
            <AppPageHeader
              title="Tenant Schemas"
              description={`Manage the tenant schemas of ${cloud?.Name}.`}
              actionButtonName="Clean Schemas"
              actionButtonColor="primary"
              action={() => setOpen(true)}
            />
          </Container>
        </div>
        {recordsLoading ? (
          <>
            <SkeletonLoader height={200} />
          </>
        ) : (
          <>
            {records.length > 0 ? (
              <div className="px-3">
                {rows}
                <div className="mt-4">
                  <AppTablePagination
                    pagination={pagination}
                    records={records}
                    query={query}
                    setQuery={setQuery}
                    loading={recordsLoading}
                    error={error}
                    module={module}
                    setSelectedRecords={() => {}}
                  />
                </div>
              </div>
            ) : (
              <AppEmptyState
                title="No Schemas Found"
                description="There are no tenant schemas in this cloud."
                onClick={() => {}}
              />
            )}
          </>
        )}
      </div>
      <AppDrawer
        size="sm"
        placement="right"
        open={open}
        onClose={() => setOpen(false)}
        backdrop={false}
        headerTitle="Clear Tenant Schemas"
        headerOptions={
          <Button
            disabled={cleanTenantSchemasLoading || quantity < 1}
            size="sm"
            color="primary"
            onClick={handleCleanSchemas}
          >
            {cleanTenantSchemasLoading ? (
              <AppSpinner size="sm" color="white" />
            ) : (
              "Clean"
            )}
          </Button>
        }
        bodyCSS="p-4"
        body={
          <div>
            <h5 className="mb-3">
              Select the number of Tenant Schemas that will remain after
              deletion
            </h5>
            <InputGroup className="w-50">
              <InputGroup.Button
                onClick={() => setQuantity((prev) => prev - 1)}
              >
                -
              </InputGroup.Button>
              <InputNumber
                value={quantity}
                onChange={(qty: any) => handleQuantityChange(qty)}
              />
              <InputGroup.Button
                onClick={() => setQuantity((prev) => prev + 1)}
              >
                +
              </InputGroup.Button>
            </InputGroup>
          </div>
        }
      />
    </>
  );
};

export default TenantSchemasList;
